import * as React from 'react';
import { Modal } from '@mc/react-core';
import { Dialog } from '../dialog/Dialog';
import { DialogBody } from '../dialog/DialogBody';
import { DialogFooter } from '../dialog/DialogFooter';
import { Button } from '../button/Button';
import { ConfirmIntent, InfoIntent, ViewIntent } from '../common/props';
import * as Classes from '../common/classes';
import classNames from 'classnames';
import { Space } from '../space/Space';
import { Title } from '../typography/Title';
import { Text } from '../typography/Text';
import { Icon } from '../icon/Icon';
import {
  mcDangerFilled,
  mcInfoFilled,
  mcSuccessFilled,
  mcWarningFilled,
} from '@mc/react-ui-icons';
import { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { ModalComponentProps } from './ModalManager';
import { ApiError, ApiErrorIntent } from '@mc/axios-api';

export type ConfirmProps = {
  // Titulek dialogu, výchozí hodnota je 'Potvrzení'
  title?: string | React.ReactNode;
  // Zpráva
  message?: React.ReactNode | (() => React.ReactNode);
  // Text potvrzovacího tlačítka, výchozí hodnota je 'Ano'
  confirmText?: string;
  // Text storno tlačítka, výchozí hodnota je 'Ne'
  cancelText?: string;
  // Pokud je uvedeno, zobrazí se třetí alternativní tlačítko
  thirdText?: string;
  // Intent potvrzovacího tlačítka
  intent?: ConfirmIntent;
  iconIntent?: ViewIntent;
};

/**
 * Zobrazení nějaké potvrzovací akce.
 *
 * @return Vraci true pokud uzivatel potvrdí, false pokud odmítne, případně 'third' při stisknutí třetího tlačítka, pokud je povolené
 */
const getIconByIntent = (intent: ViewIntent): IconDefinition => {
  switch (intent) {
    case 'danger':
      return mcDangerFilled;
    case 'warning':
      return mcWarningFilled;
    case 'success':
      return mcSuccessFilled;
    default:
      return mcInfoFilled;
  }
};

export type DeleteConfirmProps = {
  message?: string;
  confirmText?: string;
};
/**
 * Zobrazení potvrzovacího dialogu pro smazání záznamu.
 * messsage: Zpráva dialogu, výchozí hodnota je 'Odstranit záznam?'
 * confirmText: Potvrzovací tlačítko, výchozí hodnota je 'Odstranit'
 */
export const showDeleteConfirm = (props?: DeleteConfirmProps) => {
  return showConfirm({
    message: props?.message ?? 'Odstranit záznam?',
    intent: 'danger',
    confirmText: props?.confirmText ?? 'Odstranit',
    cancelText: 'Zrušit',
  });
};

export const showConfirm = ({
  title,
  message,
  intent = 'none',
  confirmText = 'Ano',
  cancelText = 'Ne',
  thirdText,
  iconIntent = 'warning',
}: ConfirmProps): Promise<boolean | 'third'> => {
  return new Promise<boolean | 'third'>((resolve) => {
    let cls = classNames(Classes.CONFIRM_DIALOG, {
      [Classes.CONFIRM_DIALOG_TITLE_OR_MESSAGE]: !(title && message),
    });

    Modal.show(({ onClose }) => (
      <Dialog
        dynamicMinWidth={270}
        dynamicMaxWidth={450}
        size={'dynamic'}
        className={cls}
        portalClassName={Classes.DIALOG_TOP}
      >
        <DialogBody>
          <Space className={Classes.CONFIRM_DIALOG_BODY} vertical size={'2'}>
            <Icon
              size={'2x'}
              icon={getIconByIntent(iconIntent)}
              intent={iconIntent}
            />
            <Title type={'block'} className={'text-center'}>
              {!title && !message
                ? 'Opravdu chcete provést danou operaci?'
                : title || message}
            </Title>
            <div className={Classes.CONFIRM_DIALOG_MESSAGE}>
              {message && title && (
                <Text>
                  {typeof message === 'function' ? message() : message}
                </Text>
              )}
            </div>
          </Space>
        </DialogBody>
        <DialogFooter noButtonToolbar>
          <Space
            className={classNames({
              [Classes.CONFIRM_DIALOG_REVERSE_BUTTONS]: !Boolean(thirdText),
            })}
            vertical={Boolean(thirdText)}
            size={thirdText ? '1' : '2'}
          >
            <Button
              dataTest={'confirm'}
              danger={intent === 'danger'}
              style={'primary'}
              onClick={() => {
                onClose();
                resolve(true);
              }}
            >
              {confirmText}
            </Button>
            {thirdText && (
              <Button
                dataTest={'third'}
                className={'mb-1'}
                onClick={() => {
                  onClose();
                  resolve('third');
                }}
              >
                {thirdText}
              </Button>
            )}
            <Button
              dataTest={'cancel'}
              onClick={() => {
                onClose();
                resolve(false);
              }}
            >
              {cancelText}
            </Button>
          </Space>
        </DialogFooter>
      </Dialog>
    ));
  });
};

const convertIntent = (apiIntent?: ApiErrorIntent): InfoIntent => {
  if (!apiIntent) {
    return 'warning';
  }
  switch (apiIntent) {
    case ApiErrorIntent.WARNING:
    case ApiErrorIntent.DANGER:
      return 'warning';
    default:
      return 'primary';
  }
};

export const showApiError = (error: ApiError): Promise<boolean | 'third'> => {
  let intent = convertIntent(error.errorIntent);

  const hasNewLines =
    error.messageTitle?.includes('\n') ||
    error.messageDesc?.includes('\n') ||
    error.detailErrors?.some((e) => e.includes('\n'));

  return showInfo({
    intent: intent,
    title: error.messageTitle?.split('\n').map((msg) => <div>{msg}</div>),
    alignLeft: hasNewLines,
    message: (
      <div>
        {error.messageDesc && (
          <div className={'mt-1'}>
            {error.messageDesc.split('\n').map((msg) => (
              <div>{msg}</div>
            ))}
          </div>
        )}
        {error.detailErrors && (
          <div className={'mt-1'}>
            {error.detailErrors.map((e) => {
              return (
                <div>
                  <small>{e}</small>
                </div>
              );
            })}
          </div>
        )}
      </div>
    ),
  });
};

export const showValidationApiError = (
  error: ApiError
): Promise<boolean | 'third'> => {
  const { _error, ...rest } = error.formErrors;

  const errObj = {
    ...rest,
  };
  if (_error) {
    errObj['_global'] = _error;
  }

  let intent: InfoIntent = convertIntent(error.errorIntent);

  return showInfo({
    intent: intent,
    title: 'Nastaly chyby při zpracování požadavku',
    message: (
      <div>
        {errObj['_global'] && <small>{errObj['_global']}</small>}
        {Object.keys(errObj).map((key: any) => {
          return (
            <div>
              {key !== '_global' && (
                <small>
                  {errObj[key]} ({key})
                </small>
              )}
            </div>
          );
        })}
      </div>
    ),
  });
};

export const showUnknownApiError = (): Promise<boolean | 'third'> => {
  return showInfo({ title: 'Nastala neočekávaná chyba' });
};

export type InfoProps = {
  /**
   * Pokud je true, text bude zarovnán vlevo.
   */
  alignLeft?: boolean;
  // Titulek dialogu, výchozí hodnota je 'Potvrzení'
  title?: string | React.ReactNode;
  // Zpráva
  message?: React.ReactNode | (() => React.ReactNode);
  // Text potvrzovacího tlačítka, výchozí hodnota je 'OK'
  confirmText?: string;
  // Intent ikony, výchozí hodnota je warning
  intent?: InfoIntent;
};
export const showInfo = ({
  title,
  message,
  intent,
  alignLeft,
  confirmText = 'OK',
}: InfoProps): Promise<boolean | 'third'> => {
  return new Promise<boolean | 'third'>((resolve) => {
    let cls = classNames(Classes.CONFIRM_DIALOG, {
      [Classes.CONFIRM_DIALOG_TITLE_OR_MESSAGE]: !(title && message),
    });

    Modal.show(({ onClose }) => (
      <InnerInformationModal
        alignLeft={alignLeft}
        onClose={onClose}
        cls={cls}
        resolve={resolve}
        intent={intent}
        title={title}
        confirmText={confirmText}
        message={message}
      />
    ));
  });
};
const InnerInformationModal: React.FC<
  InfoProps & {
    cls: string;
    resolve: (
      value: boolean | 'third' | PromiseLike<boolean | 'third'>
    ) => void;
  } & ModalComponentProps
> = ({
  title,
  message,
  intent = 'warning',
  confirmText,
  cls,
  alignLeft,
  resolve,
  onClose,
}) => {
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const [tempDisable, setTempDisable] = React.useState<boolean>(true);
  React.useEffect(() => {
    setTimeout(() => {
      setTempDisable(false);
      buttonRef.current?.focus();
    }, 500);
  }, []);
  return (
    <Dialog
      dynamicMaxWidth={640}
      dynamicMinWidth={270}
      size={'dynamic'}
      className={cls}
      portalClassName={Classes.DIALOG_TOP}
      escapeKeyClose
      onClose={onClose}
    >
      <DialogBody>
        <Space
          className={classNames(Classes.CONFIRM_DIALOG_BODY, {
            [Classes.CONFIRM_DIALOG_BODY_LEFT]: alignLeft,
          })}
          vertical
          size={'2'}
        >
          <Icon
            className={'align-self-center'}
            size={'2x'}
            intent={intent}
            icon={getIconByIntent(intent)}
          />
          <Title className={!alignLeft ? 'text-center' : ''} type={'block'}>
            {!title && !message ? 'Operace se nepovedla' : title || message}
          </Title>
          <div className={Classes.CONFIRM_DIALOG_MESSAGE}>
            {message && title && (
              <Text className={!alignLeft ? 'text-center' : ''}>
                {typeof message === 'function' ? message() : message}
              </Text>
            )}
          </div>
        </Space>
      </DialogBody>
      <DialogFooter noButtonToolbar>
        <Button
          dataTest={'confirm'}
          style={'primary'}
          disabled={tempDisable}
          ref={buttonRef}
          onClick={() => {
            onClose();
            resolve(false);
          }}
        >
          {confirmText}
        </Button>
      </DialogFooter>
    </Dialog>
  );
};
